import { Chip } from "@mui/material";
import { alpha } from "@mui/material/styles";

/**
 * Shared status chip for resolvable findings — generator-status exceedances and
 * manifest discrepancies share the same UNRESOLVED / PENDING / RESOLVED / IGNORED
 * lifecycle (the GraphQL `ResolutionStatus` enum), so they share one chip and one
 * color mapping (ENG-15697).
 *
 * Built to the design-system chip spec (.cursor/rules/design-system.mdc): MUI
 * semantic palette colors in the light-filled variant (role `main` at 8% alpha +
 * colored label), never the solid dark-fill default and never the encamp* brand
 * palette. IGNORED (and anything unmapped) renders as a plain outlined grey chip.
 *
 * `status` is the ResolutionStatus value (its runtime value is the SCREAMING_SNAKE
 * string, shared by the Prisma- and GraphQL-generated enums). Pass `label` to
 * override the displayed text (e.g. a richer stage label like "Validated · Ready
 * for Pickup", or a linked submission's live status). Pass `role` to override the
 * color when a sub-state needs its own hue — e.g. a validated (Ready for Pickup)
 * exceedance is still `UNRESOLVED` but should read as distinct from "Needs
 * Validation" (ENG-16017).
 */
type ChipRole = "warning" | "info" | "success" | "error";

const STATUS_ROLE: Record<string, ChipRole> = {
  UNRESOLVED: "warning",
  PENDING: "info",
  RESOLVED: "success",
  // IGNORED (and anything unmapped) → neutral grey, handled below.
};

export function ResolutionStatusChip({
  status,
  label,
  role: roleOverride,
}: {
  status: string;
  label: string;
  role?: ChipRole;
}) {
  const role = roleOverride ?? STATUS_ROLE[status];

  if (!role) {
    return <Chip size="small" variant="outlined" label={label} />;
  }

  return (
    <Chip
      size="small"
      label={label}
      sx={{
        color: `${role}.main`,
        bgcolor: (theme) => alpha(theme.palette[role].main, 0.08),
      }}
    />
  );
}
